import { useState } from 'react'
import { type Path } from 'react-hook-form'
import { cn } from '@/lib/utils'
import { ChevronDown } from '@/components/icons'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible'
import type { FlightLogFormData } from '../form'
import type { FlightLogSectionContext } from '../useFlightLogForm'
import { NumRow } from '../../ui/prntr-fields'
import { PrntrGroup } from '../../ui/prntr-input'
import { SectionLabel } from '../../ui/prntr-input'

const engineReadings = [
  ['Fuel Flow', 'fuelFlow'],
  ['N1/Prop', 'n1Pct'],
  ['N2/NG', 'n2Pct'],
  ['ITT', 'itt'],
  ['Oil Press', 'oilPress'],
  ['Oil Temp', 'oilTemp'],
  ['Fuel Temp', 'fuelTemp'],
  ['Bleed Press', 'bleedPress'],
] as const

export function EctmSection({ context }: { context: FlightLogSectionContext }) {
  const { control, values, readOnly } = context
  const [openEngines, setOpenEngines] = useState<Record<number, boolean>>({ 0: true })
  return (
    <>
      <SectionLabel>Flight conditions at cruise</SectionLabel>
      <PrntrGroup>
        <NumRow control={control} name="ectm.weightKg" label="Weight" suffix="KG" readOnly={readOnly} />
        <NumRow control={control} name="ectm.oatSatC" label="OAT/SAT" suffix="°C" readOnly={readOnly} />
        <NumRow control={control} name="ectm.iasKt" label="IAS" suffix="KT" readOnly={readOnly} />
        <NumRow control={control} name="ectm.tasKt" label="TAS" suffix="KT" readOnly={readOnly} />
        <NumRow control={control} name="ectm.mach" label="Mach" readOnly={readOnly} />
        <NumRow
          control={control}
          name="ectm.flightLevel"
          label="FL"
          integer
          placeholder="e.g. 410"
          readOnly={readOnly}
        />
      </PrntrGroup>
      <SectionLabel>Per-engine readings</SectionLabel>
      <div className="flex flex-col">
        {values.ectm.engines.map((_, i) => (
          <Collapsible
            key={`ectm-engine-${i}`}
            open={!!openEngines[i]}
            onOpenChange={(open) => setOpenEngines((prev) => ({ ...prev, [i]: open }))}
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex min-h-12 w-full items-center justify-between border-b border-border-L1 text-left"
              >
                <span className="text-sm font-semibold text-content-primary-bold">
                  Engine {i + 1}
                </span>
                <ChevronDown
                  className={cn(
                    'size-4 shrink-0 text-content-primary-subtle transition-transform',
                    openEngines[i] && 'rotate-180'
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <PrntrGroup className="pl-2 [&>*:last-child]:border-b">
                {engineReadings.map(([label, key]) => (
                  <NumRow
                    key={`ectm-${key}-${i}`}
                    control={control}
                    name={`ectm.engines.${i}.${key}` as Path<FlightLogFormData>}
                    label={label}
                    readOnly={readOnly}
                  />
                ))}
              </PrntrGroup>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </>
  )
}
